import { React, useState } from 'react';

import { FormHelperText } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import TextField from '@mui/material/TextField';
import AccountBalanceWalletRoundedIcon from '@mui/icons-material/AccountBalanceWalletRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import QrCode2RoundedIcon from '@mui/icons-material/QrCode2Rounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import RequestQuoteRoundedIcon from '@mui/icons-material/RequestQuoteRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { pt } from 'date-fns/locale';

import { Alerta } from '../../components/alerta';
import DayDatePicker from '../../components/dayDatePicker';
import PageHeader from '../../components/pageHeader';
import SubmitButton from '../../components/submitButton';
import {
    formatDateLabelPtBr,
    formatIsoDate,
    formatMonthYearLabelPtBr
} from '../../utils/dateUtils';
import { fetchFlask } from '../../utils/fetchFlask';
import { formatCurrency } from '../../utils/stringFormat';
import '../css/overduePix.css';

const hasNumericValue = (value) => value !== '' && value !== null && !Number.isNaN(Number(value));

const GerarPix = () => {
    const [cc, setCC] = useState('');
    const [dateTime, setDateTime] = useState(null);
    const [dateTimePag, setDateTimePag] = useState(null);
    const [error, setError] = useState(false);
    const [errorPag, setErrorPag] = useState(false);
    const [valorConta, setValorConta] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    async function submitGerarPix (e) {
        e.preventDefault();
        setIsSubmit(true);

        let periodo = '';
        if (dateTime) {
            setError(false);
            const ano = dateTime.getFullYear();
            const mes = dateTime.getMonth() + 1;
            periodo = ano + '-' + mes;
        } else {
            setError(true);
            setIsSubmit(false);
            return;
        }

        let dataPagamento = '';
        if (dateTimePag) {
            setErrorPag(false);
            dataPagamento = formatIsoDate(dateTimePag);
        } else {
            setErrorPag(true);
            setIsSubmit(false);
            return;
        }

        const body = JSON.stringify({
            cc,
            periodo,
            valor: valorConta,
            pagamento: dataPagamento
        });

        try {
            const response = await fetchFlask('gerarPix', body);
            setMessage(response.message);
            setStatus(response.status);
            setOpenAlerta(true);
            if (response.status === 'success') {
                setCC('');
                setDateTime(null);
                setDateTimePag(null);
                setValorConta('');
            }
        } finally {
            setIsSubmit(false);
        }
    };

    const periodoLabel = formatMonthYearLabelPtBr(dateTime, 'Pendente');
    const periodoPayloadLabel = dateTime ? `${dateTime.getFullYear()}-${dateTime.getMonth() + 1}` : 'Não gerado';
    const pagamentoLabel = formatDateLabelPtBr(dateTimePag);
    const valorLabel = hasNumericValue(valorConta) ? formatCurrency(Number(valorConta)) : 'Não informado';
    const completedFields = [
        cc,
        dateTime,
        hasNumericValue(valorConta),
        dateTimePag
    ].filter(Boolean).length;

    return (
        <Box className='mainBox overdue-pix-page'>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />
            <PageHeader>
                Gerar PIX - Boleto Baixado
            </PageHeader>

            <form action='' method='POST' id='formCancelarOS' onSubmit={submitGerarPix} className='overdue-pix-layout'>
                <Box className='overdue-pix-workspace'>
                    <Box className='overdue-pix-intro'>
                        <Box>
                            <h2>PIX para pagamento de boleto vencido</h2>
                        </Box>
                        <p>
                            Informe a conta contrato, competência, valor original e nova data de pagamento antes de gerar o PIX.
                        </p>
                    </Box>

                    <Box className='overdue-pix-panel'>
                        <Box className='overdue-pix-section-heading'>
                            <ReceiptLongRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Identificação do boleto</strong>
                            </Box>
                        </Box>

                        <Box className='overdue-pix-document-strip'>
                            <RequestQuoteRoundedIcon />
                            <TextField
                                required
                                label='Conta Contrato'
                                value={cc}
                                onChange={(e) => setCC(e.target.value)}
                            />
                        </Box>
                    </Box>

                    <Box className='overdue-pix-panel'>
                        <Box className='overdue-pix-section-heading'>
                            <CalendarMonthRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Competência e valor original</strong>
                            </Box>
                        </Box>

                        <Box className='overdue-pix-fields-grid'>
                            <FormControl className='overdue-pix-date-control' error={error}>
                                <LocalizationProvider adapterLocale={pt} dateAdapter={AdapterDateFns}>
                                    <DatePicker
                                        id='data'
                                        label='Período'
                                        views={['month', 'year']}
                                        openTo='month'
                                        minDate={new Date('2024-01-01')}
                                        maxDate={new Date()}
                                        value={dateTime}
                                        onChange={(value) => setDateTime(value)}
                                        slotProps={{
                                            openPickerButton: {
                                                color: 'primary'
                                            }
                                        }}
                                    />
                                </LocalizationProvider>
                                {error && (
                                    <FormHelperText>Por favor selecione uma data.</FormHelperText>
                                )}
                            </FormControl>

                            <TextField
                                required
                                label='Valor Original do Boleto'
                                type='number'
                                inputProps={{ step: 0.01, min: 0 }}
                                value={valorConta}
                                onChange={(e) => setValorConta(e.target.value)}
                            />
                        </Box>
                    </Box>

                    <Box className='overdue-pix-panel'>
                        <Box className='overdue-pix-section-heading'>
                            <PaymentsRoundedIcon />
                            <Box>
                                <span>Etapa 3</span>
                                <strong>Nova data de pagamento</strong>
                            </Box>
                        </Box>

                        <Box className='overdue-pix-payment-strip'>
                            <AccountBalanceWalletRoundedIcon />
                            <FormControl className='overdue-pix-date-control' error={errorPag}>
                                <DayDatePicker
                                    id='dataPagamento'
                                    label='Data de Pagamento'
                                    minDate={new Date('2024-06-01')}
                                    value={dateTimePag}
                                    onChange={setDateTimePag}
                                />
                                {errorPag && (
                                    <FormHelperText>Por favor selecione uma data.</FormHelperText>
                                )}
                            </FormControl>
                        </Box>
                    </Box>

                    <Box className='overdue-pix-rail'>
                        <Box>
                            <WarningAmberRoundedIcon />
                            <span>Origem</span>
                            <strong>Boleto vencido</strong>
                        </Box>
                        <Box>
                            <QrCode2RoundedIcon />
                            <span>Destino</span>
                            <strong>PIX de regularização</strong>
                        </Box>
                    </Box>
                </Box>

                <Box className='overdue-pix-summary'>
                    <Box className='overdue-pix-summary-header'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão do PIX</strong>
                        </Box>
                    </Box>

                    <Box className='overdue-pix-summary-list'>
                        <Box>
                            <span>Conta contrato</span>
                            <strong className={!cc ? 'is-empty' : undefined}>{cc || 'Não informada'}</strong>
                        </Box>
                        <Box>
                            <span>Competência</span>
                            <strong className={!dateTime ? 'is-empty' : undefined}>{periodoLabel}</strong>
                        </Box>
                        <Box>
                            <span>Período enviado</span>
                            <strong className={!dateTime ? 'is-empty' : undefined}>{periodoPayloadLabel}</strong>
                        </Box>
                        <Box>
                            <span>Valor original</span>
                            <strong>{valorLabel}</strong>
                        </Box>
                        <Box>
                            <span>Pagamento</span>
                            <strong className={!dateTimePag ? 'is-empty' : undefined}>{pagamentoLabel}</strong>
                        </Box>
                    </Box>

                    <Box className='overdue-pix-attention'>
                        <WarningAmberRoundedIcon />
                        <p>
                            O PIX será gerado para regularizar o boleto baixado, usando a conta contrato, período e data de pagamento informados.
                        </p>
                    </Box>

                    <Box className='overdue-pix-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/4 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<QrCode2RoundedIcon />}>
                        Gerar PIX
                    </SubmitButton>
                </Box>
            </form>
        </Box>
    );
};

export default GerarPix;
